import { Alert } from '@mui/material';
import { createColumnHelper, type OnChangeFn, type PaginationState } from '@tanstack/react-table';
import { memo, useMemo } from 'react';

import BaseTable from '@/components/shared/Table/BaseTable';
import useBaseTable from '@/components/shared/Table/hooks/useBaseTable';
import type { AuditLogItem } from '@/hooks/useAuditLogs';

const columnHelper = createColumnHelper<AuditLogItem>();

const formatPerformedAt = (dateTime: string): string => {
  if (!dateTime) {
    return '-';
  }

  const parsed = new Date(dateTime);

  if (Number.isNaN(parsed.getTime())) {
    return dateTime;
  }

  return parsed.toISOString().replace('T', ' ').replace('Z', ' +00:00');
};

export interface AuditLogsTableProps {
  auditLogs: AuditLogItem[];
  pagination: PaginationState;
  totalCount: number;
  isLoading: boolean;
  error: Error | null | undefined;
  onPaginationChange: OnChangeFn<PaginationState>;
}

function AuditLogsTableComponent({ auditLogs, pagination, totalCount, isLoading, error, onPaginationChange }: AuditLogsTableProps) {
  const columns = useMemo(
    () => [
      columnHelper.accessor('performedAt', {
        header: 'Performed At',
        size: 260,
        cell: (info) => formatPerformedAt(info.getValue() ?? ''),
      }),
      columnHelper.accessor('performedBy', {
        header: 'Performed By',
        size: 260,
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('action', {
        header: 'Action',
        size: 120,
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('userId', {
        header: 'User Email',
        size: 260,
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('role', {
        header: 'Role',
        size: 140,
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('groupName', {
        header: 'Group Name',
        size: 220,
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('groupType', {
        header: 'Group Type',
        size: 220,
        cell: (info) => info.getValue() || '-',
      }),
    ],
    [],
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    onPaginationChange((prev) => ({ ...prev, pageIndex: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    onPaginationChange(() => ({ pageIndex: 0, pageSize: newPageSize }));
  };

  const tableWithData = useBaseTable<AuditLogItem>({
    tableData: auditLogs,
    columns,
    isRowSelectionEnabled: false,
    isColumnVisibilityEnabled: false,
    isColumnOrderEnabled: false,
    hasPagination: true,
    pagination,
    onPaginationChange,
    paginationCount: totalCount,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [10, 50, 100],
    showFirstButton: true,
    showLastButton: true,
  });

  if (error) {
    return (
      <Alert
        severity='error'
        sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px' }}
        data-testid='error-loading-audit-logs'
      >
        Error loading Audit Logs: {error.message}
      </Alert>
    );
  }

  return (
    <BaseTable<AuditLogItem>
      {...tableWithData}
      testId='audit-logs-table'
      ariaLabel='Audit logs list'
      noDataMessage='No audit logs found.'
      loadingData={{
        loadingMessage: 'Loading Audit Logs…',
        isLoading: isLoading,
      }}
    />
  );
}

const AuditLogsTable = memo(AuditLogsTableComponent);

export default AuditLogsTable;
